import { API_BASE_URL } from '../config/api';

class AdminAuthService {
  constructor() {
    this.token = null;
    this.user = null;
    this.initialized = false;
    this.initializeAuth();
  }

  // Initialize authentication from localStorage
  initializeAuth() {
    try {
      // Always read fresh from localStorage
      const storedToken = localStorage.getItem('admin_jwt_token');
      if (storedToken) {
        this.token = storedToken;
        this.loadUserFromToken();
      }
      this.initialized = true;
    } catch (error) {
      console.error('Error initializing auth:', error);
      this.initialized = true;
    }
  }

  // Decode JWT token to get user info
  decodeToken(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  }

  // Load user data from stored token
  loadUserFromToken() {
    if (this.token) {
      const decoded = this.decodeToken(this.token);
      if (decoded && decoded.exp > Date.now() / 1000 && decoded.admin) {
        this.user = decoded;
      } else {
        // Token expired or not admin
        this.logout();
      }
    }
  }

  // Check if admin is authenticated
  isAuthenticated() {
    // Re-check localStorage if token is missing
    if (!this.token || !this.user) {
      const storedToken = localStorage.getItem('admin_jwt_token');
      if (storedToken && storedToken !== this.token) {
        this.token = storedToken;
        this.loadUserFromToken();
      }
    }
    return this.token && this.user && this.user.exp > Date.now() / 1000 && this.user.admin;
  }

  // Check if user has admin role
  isAdmin() {
    // Re-check authentication if user is missing
    if (!this.user && this.token) {
      this.loadUserFromToken();
    }
    return this.user && this.user.admin && ['admin', 'employee', 'super_admin', 'packaging_admin', 'inventory_admin'].includes(this.user.role);
  }

  // Get current admin user
  getCurrentUser() {
    // Ensure we have the latest user data
    if (!this.user && this.token) {
      this.loadUserFromToken();
    }
    return this.user;
  }

  // Get the stored JWT token
  getToken() {
    return this.token;
  }

  // Admin login with JWT
  async login(username, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin_login_jwt.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Admin login failed');
      }

      if (data.success && data.token) {
        // Store token and user data
        this.token = data.token;
        localStorage.setItem('admin_jwt_token', this.token);
        
        // Decode token to get user info
        this.user = this.decodeToken(this.token);
        
        return {
          success: true,
          admin: data.admin,
          token: data.token
        };
      } else {
        throw new Error(data.error || 'Admin login failed');
      }
    } catch (error) {
      throw error;
    }
  }

  // Logout
  logout() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('admin_jwt_token');
  }

  // Get auth headers for API requests
  getAuthHeaders() {
    if (this.token) {
      return {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json'
      };
    }
    return {
      'Content-Type': 'application/json'
    };
  }

  // Make authenticated API request
  async authenticatedFetch(url, options = {}) {
    if (!this.isAuthenticated()) {
      throw new Error('Admin not authenticated');
    }

    const authOptions = {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers
      }
    };

    const response = await fetch(url, authOptions);
    
    if (response.status === 401) {
      // Token expired or invalid
      this.logout();
      throw new Error('Admin authentication expired. Please login again.');
    }

    return response;
  }

  // Get dashboard statistics
  async getDashboardStats() {
    try {
      const response = await this.authenticatedFetch(`${API_BASE_URL}/api/admin_dashboard_simple.php`);
      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || 'Failed to get dashboard stats');
      }
    } catch (error) {
      throw error;
    }
  }

  // Get users with pagination
  async getUsers(page = 1, limit = 20, search = '') {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: search
      });
      
      const response = await this.authenticatedFetch(`${API_BASE_URL}/api/admin_users.php?${params}`);
      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || 'Failed to get users');
      }
    } catch (error) {
      throw error;
    }
  }

  // Update user status
  async updateUserStatus(userId, isActive) {
    try {
      const response = await this.authenticatedFetch(`${API_BASE_URL}/api/admin_users.php`, {
        method: 'PUT',
        body: JSON.stringify({ user_id: userId, is_active: isActive })
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || 'Failed to update user status');
      }
    } catch (error) {
      throw error;
    }
  }

  // Delete user (admin only)
  async deleteUser(userId) {
    try {
      const response = await this.authenticatedFetch(`${API_BASE_URL}/api/admin_users.php`, {
        method: 'DELETE',
        body: JSON.stringify({ user_id: userId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || 'Failed to delete user');
      }
    } catch (error) {
      throw error;
    }
  }
}

// Create singleton instance
const adminAuthService = new AdminAuthService();
export default adminAuthService;
